import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AppLogo from "@/components/AppLogo";
import LiveClock from "@/components/LiveClock";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(err.sessionBlocked ? err.message : apiError(err, "Login gagal. Periksa email dan password Anda."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      <div
        className="hidden lg:flex lg:w-[52%] flex-col justify-between p-12 text-white relative overflow-hidden"
        style={{ background: "linear-gradient(140deg,#1e5f66 0%,#267B84 35%,#00B9AD 80%,#60C0D0 100%)" }}
      >
        <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-white/10" />
        <div className="absolute -right-10 bottom-10 h-56 w-56 rounded-full bg-[#CDDC29]/15" />
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <AppLogo className="h-11 w-11" />
            <div>
              <p className="font-heading font-bold text-lg leading-tight">TERPADU-OP RSCM</p>
              <p className="text-xs text-white/70">Rumah Sakit Cipto Mangunkusumo</p>
            </div>
          </div>
        </div>
        <div className="relative z-10 space-y-8">
          <div>
            <h1 className="font-heading text-3xl xl:text-4xl font-bold leading-tight">
              Sistem Terpadu Operasi<br />Persiapan Masuk Rawat Inap
            </h1>
          </div>
        </div>
        <p className="relative z-10 text-xs text-white/60">© 2026 RSCM — Sistem Internal</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <AppLogo />
            <div>
              <p className="font-heading font-bold text-slate-900">TERPADU-OP RSCM</p>
              <p className="text-xs text-slate-500">Jadwal Pasien Rawat Inap</p>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-9" data-testid="login-card">
            <LiveClock className="mb-4" />
            <h2 className="font-heading text-2xl font-bold text-slate-900">Masuk ke Akun</h2>
            <p className="text-sm text-slate-500 mt-1 mb-7">Gunakan akun yang terdaftar di sistem.</p>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="email@contoh.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="h-11"
                />
              </div>
              {error && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3.5 py-2.5" data-testid="login-error">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit-button"
                className="w-full h-11 bg-[#00B9AD] hover:bg-[#009E94] text-white font-medium transition-all active:scale-[0.98]"
              >
                {loading ? "Memproses..." : "Masuk"}
              </Button>
            </form>
          </div>
          <p className="text-center text-xs text-slate-400 mt-6">
            Lupa password? Hubungi Super Admin untuk reset akun.
          </p>
        </div>
      </div>
    </div>
  );
}
